import React from 'react';
import { LocalStorageService } from '@uplift/services';


export const useLocalStorageValue = <T>(key: string, initialValue: T) => {
  const [ value, setValue ] = React.useState<T | undefined>(() => {
    return LocalStorageService.getItem<T>(key) ?? initialValue;
  });

  const setValueWithStorage = React.useCallback((newValue: T) => {
    LocalStorageService.setItemWithEmit(key, newValue);
  }, [ key ]);

  React.useEffect(() => {
    const handleStorage = (newValue: T) => {
      setValue(newValue);
    };

    LocalStorageService.addValueChangeListener<T>(key, handleStorage);

    return () => {
      LocalStorageService.removeValueChangeListener<T>(key, handleStorage);
    };
  }, [ key ]);

  return [ value, setValueWithStorage ] as const;
};

export const useLocalStorageValueChange = <T>(key: string, callback: (newValue: T | undefined, oldValue: T | undefined) => void) => {
  const callbackRef = React.useRef(callback);

  callbackRef.current = callback;

  React.useEffect(() => {
    const handleStorage = (newValue: T, oldValue?: T) => {
      callbackRef.current?.(newValue, oldValue);
    };

    LocalStorageService.addValueChangeListener<T>(key, handleStorage);

    return () => {
      LocalStorageService.removeValueChangeListener<T>(key, handleStorage);
    };
  }, [ key ]);
};


export const useTabsSyncedState = <T>(key: string, defaultValue?: T, options?: {
  overrideWithDefaultOnMount?: boolean;
}) => {
  const [ value, setValue ] = React.useState<T | undefined>(() => {
    if (options?.overrideWithDefaultOnMount) {
      LocalStorageService.setItem(key, defaultValue);
      return defaultValue;
    }

    const storageValue = LocalStorageService.getItem<T>(key);

    if (storageValue === undefined || storageValue === null) {
      LocalStorageService.setItem(key, defaultValue);
      return defaultValue;
    }

    return storageValue;
  });

  const handleSetValue: typeof setValue = React.useCallback((value) => {
    LocalStorageService.setItemWithEmit(key, value);
  }, [ key ]);

  useLocalStorageValueChange<T>(key, (value) => {
    setValue(value);
  });

  return [ value, handleSetValue ] as const;
};


export const useTabsSyncedCallbackTrigger = (key: string) => {
  return () => {
    LocalStorageService.setItemWithEmit(key, Date.now());
  };
};


export const useTabsSyncedCallback = (key: string, callback: () => void) => {
  const callbackRef = React.useRef(callback);

  useLocalStorageValueChange<number>(key, () => {
    callbackRef.current?.();
  });

  return useTabsSyncedCallbackTrigger(key);
};


export const useLocalStorageValueChangeEffect = <T>(key: string, callback: (value?: T, isInitial?: boolean) => void, deps?: React.DependencyList) => {
  const [ value ] = useTabsSyncedState<T>(key);
  const callbackRef = React.useRef(callback);
  const initialRef = React.useRef(true);

  callbackRef.current = callback;
  React.useEffect(() => {
    const cleanup = callbackRef.current?.(value, initialRef.current);

    initialRef.current = false;
    return cleanup;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps ? [ value, ...deps ] : [ value ]);
};
